<template>
  <div class="gs-root">
    <div class="gs-card">
      <div class="gs-card-header">
        <div class="gs-card-header-left">
          <div class="gs-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ff825d"
              stroke-width="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <h2 class="gs-title">Gestão de Equipa</h2>
            <p class="gs-sub">
              Convida colaboradores e define o acesso por restaurante
            </p>
          </div>
        </div>
        <div
          class="gs-limite-badge"
          :class="limiteAtingido ? 'badge-warn' : 'badge-ok'"
        >
          <span v-if="!loading">{{ totalAtivo }} / {{ limite }} lugares</span>
          <span v-else>…</span>
        </div>
      </div>

      <div v-if="loading" class="gs-loading">
        <span class="gs-loading-spinner"></span>A carregar…
      </div>

      <template v-else>
        <!-- CONVIDAR -->
        <div class="gs-section">
          <h3 class="gs-section-title">Convidar colaborador</h3>
          <div class="gs-invite-row">
            <input
              v-model="emailConvite"
              type="email"
              placeholder="email@colaborador.pt"
              class="gs-input"
              :disabled="limiteAtingido || enviandoConvite"
              @keyup.enter="convidar"
            />
            <!-- Selector de role -->
            <div class="gs-role-select">
              <button
                class="gs-role-btn"
                :class="{ active: roleConvite === 'staff' }"
                @click="roleConvite = 'staff'"
              >
                Staff
              </button>
              <button
                class="gs-role-btn"
                :class="{ active: roleConvite === 'manager' }"
                @click="roleConvite = 'manager'"
              >
                Manager
              </button>
            </div>
            <button
              class="gs-btn-primary"
              :disabled="limiteAtingido || enviandoConvite || !emailConvite"
              @click="convidar"
            >
              <span v-if="enviandoConvite">A enviar…</span>
              <span v-else>Enviar convite</span>
            </button>
          </div>

          <!-- Descrição do role selecionado -->
          <p class="gs-role-desc" v-if="roleConvite === 'manager'">
            <strong>Manager</strong> — acesso total a todos os restaurantes,
            campanhas e definições. Pode gerir staff. Não pode adicionar ou
            remover restaurantes.
          </p>
          <p class="gs-role-desc" v-else>
            <strong>Staff</strong> — acesso apenas aos restaurantes que lhe
            atribuíres. Sem campanhas nem definições.
          </p>

          <p v-if="limiteAtingido" class="gs-warn-text">
            Atingiste o limite de {{ limite }} membros.
          </p>
          <p v-if="erroConvite" class="gs-error-text">{{ erroConvite }}</p>
          <p v-if="sucessoConvite" class="gs-success-text">
            {{ sucessoConvite }}
          </p>
        </div>

        <!-- CONVITES PENDENTES -->
        <div v-if="convitesPendentes.length" class="gs-section">
          <h3 class="gs-section-title">Convites pendentes</h3>
          <div class="gs-list">
            <div
              v-for="c in convitesPendentes"
              :key="c.id"
              class="gs-item gs-item-pending"
            >
              <div class="gs-item-avatar gs-item-avatar-pending">✉️</div>
              <div class="gs-item-info">
                <span class="gs-item-nome">{{ c.email }}</span>
                <span
                  class="gs-role-badge"
                  :class="
                    c.role === 'manager' ? 'badge-manager' : 'badge-staff'
                  "
                >
                  {{ c.role === "manager" ? "Manager" : "Staff" }}
                </span>
                <span class="gs-item-sub"
                  >Expira {{ formatarData(c.expira_em) }}</span
                >
              </div>
              <div class="gs-item-actions">
                <button class="gs-btn-danger-sm" @click="cancelarConvite(c)">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- EQUIPA ATIVA -->
        <div class="gs-section">
          <h3 class="gs-section-title">
            Equipa ativa
            <span class="gs-count" v-if="staff.length"
              >({{ staff.length }})</span
            >
          </h3>

          <div v-if="!staff.length" class="gs-empty">
            <p>
              Ainda não tens colaboradores. Usa o formulário acima para
              convidar.
            </p>
          </div>

          <div v-else class="gs-list">
            <div v-for="s in staff" :key="s.user_id" class="gs-item">
              <div
                class="gs-item-avatar"
                :class="s.role === 'manager' ? 'avatar-manager' : ''"
              >
                {{ (s.username || "?")[0].toUpperCase() }}
              </div>

              <div class="gs-item-info">
                <div class="gs-item-nome-row">
                  <span class="gs-item-nome">{{ s.username }}</span>
                  <span
                    class="gs-role-badge"
                    :class="
                      s.role === 'manager' ? 'badge-manager' : 'badge-staff'
                    "
                  >
                    {{ s.role === "manager" ? "Manager" : "Staff" }}
                  </span>
                </div>
                <span class="gs-item-sub">{{ s.email }}</span>
                <span class="gs-item-sub" v-if="s.last_login_date"
                  >Último login: {{ formatarData(s.last_login_date) }}</span
                >
                <span class="gs-item-sub gs-nunca" v-else>Nunca fez login</span>
              </div>

              <!-- Permissões por restaurante — só para staff -->
              <div class="gs-perms" v-if="s.role === 'staff'">
                <!-- Collapsed: resumo clicável -->
                <div
                  v-if="!editandoPerms[s.user_id]"
                  class="gs-perms-summary"
                  @click="abrirEditPerms(s)"
                >
                  <span class="gs-perms-pill">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                    {{ resumoPerms(s) }}
                  </span>
                  <span class="gs-perms-edit-hint">Editar</span>
                </div>
                <!-- Expanded: toggles -->
                <div v-else class="gs-perms-expanded">
                  <div
                    v-for="r in restaurantes"
                    :key="r.id"
                    class="gs-perm-toggle"
                    :class="temPermissao(s, r.id) ? 'perm-on' : 'perm-off'"
                    @click="togglePermissao(s, r.id)"
                  >
                    <span class="gs-perm-dot"></span>{{ r.nome }}
                  </div>
                  <p v-if="!restaurantes.length" class="gs-no-rest">
                    Sem restaurantes configurados
                  </p>
                </div>
              </div>

              <!-- Manager — acesso total -->
              <div class="gs-perms gs-manager-access" v-else>
                <span>Acesso a todos os restaurantes e definições</span>
              </div>

              <div class="gs-item-actions">
                <div class="gs-kebab-wrap">
                  <button
                    class="gs-kebab-btn"
                    @click.stop="toggleMenu(s.user_id)"
                  >
                    <span
                      v-if="alterandoRole[s.user_id] || guardando[s.user_id]"
                      >…</span
                    >
                    <span v-else>⋯</span>
                  </button>
                  <div v-if="menuAberto === s.user_id" class="gs-dropdown">
                    <button
                      v-if="s.role === 'staff' && editandoPerms[s.user_id]"
                      class="gs-dropdown-item item-save"
                      :disabled="guardando[s.user_id]"
                      @click="
                        guardarPermissoes(s);
                        menuAberto = null;
                        editandoPerms[s.user_id] = false;
                      "
                    >
                      {{ guardando[s.user_id] ? "…" : "✓ Guardar permissões" }}
                    </button>
                    <button
                      class="gs-dropdown-item"
                      :class="
                        s.role === 'staff' ? 'item-promote' : 'item-demote'
                      "
                      @click="alterarRole(s)"
                    >
                      {{
                        s.role === "staff"
                          ? "↑ Promover a Manager"
                          : "↓ Mover para Staff"
                      }}
                    </button>
                    <button
                      class="gs-dropdown-item item-danger"
                      @click="
                        confirmarRevogar(s);
                        menuAberto = null;
                      "
                    >
                      Revogar acesso
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- MODAL REVOGAR -->
    <div
      v-if="staffParaRevogar"
      class="gs-overlay"
      @click.self="staffParaRevogar = null"
    >
      <div class="gs-modal">
        <h3>Revogar acesso?</h3>
        <p>
          Tens a certeza que queres remover
          <strong>{{ staffParaRevogar.username }}</strong> ({{
            staffParaRevogar.email
          }}) da tua equipa?
        </p>
        <p style="color: #ff4444; font-size: 13px">
          Esta ação é irreversível. A conta será eliminada.
        </p>
        <div class="gs-modal-actions">
          <button class="gs-btn-neutral" @click="staffParaRevogar = null">
            Cancelar
          </button>
          <button class="gs-btn-danger" :disabled="revogando" @click="revogar">
            {{ revogando ? "A revogar…" : "Sim, revogar acesso" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import api from "@/api/index.js";
  import swal from "sweetalert2";

  export default {
    name: "GerirStaff",

    beforeRouteEnter(to, from, next) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.role === "staff") {
        next("/dashboard");
      } else {
        next();
      }
    },

    data() {
      return {
        loading: true,
        staff: [],
        convitesPendentes: [],
        restaurantes: [],
        limite: 0,
        totalAtivo: 0,
        emailConvite: "",
        roleConvite: "staff", // 'staff' | 'manager'
        enviandoConvite: false,
        erroConvite: "",
        sucessoConvite: "",
        permissoesLocais: {},
        guardando: {},
        alterandoRole: {},
        menuAberto: null,
        editandoPerms: {},
        staffParaRevogar: null,
        revogando: false,
      };
    },

    computed: {
      limiteAtingido() {
        return this.totalAtivo >= this.limite && this.limite > 0;
      },
    },

    mounted() {
      this.carregar();
      document.addEventListener("click", this.fecharMenu);
    },

    beforeDestroy() {
      document.removeEventListener("click", this.fecharMenu);
    },

    methods: {
      async carregar() {
        this.loading = true;
        try {
          const { data } = await api.get("/staff/lista");
          this.staff = data.staff || [];
          this.convitesPendentes = data.convites_pendentes || [];
          this.restaurantes = data.restaurantes || [];
          this.limite = data.limite || 0;
          this.totalAtivo = data.total_ativo || 0;

          const novasPerms = {};
          for (const s of this.staff) {
            if (s.role === "staff") {
              const permsAtivas = (s.permissoes || [])
                .filter((p) => p.ativo)
                .map((p) => p.restaurante_id);
              // Se não tem permissões definidas, selecionar todos por defeito
              novasPerms[s.user_id] = permsAtivas.length
                ? permsAtivas
                : (data.restaurantes || []).map((r) => r.id);
            }
          }
          this.permissoesLocais = novasPerms;
          this.editandoPerms = {};
        } catch (err) {
          console.error("Erro ao carregar staff:", err);
        } finally {
          this.loading = false;
        }
      },

      temPermissao(staff, restauranteId) {
        return (this.permissoesLocais[staff.user_id] || []).includes(
          restauranteId,
        );
      },

      togglePermissao(staff, restauranteId) {
        const atual = this.permissoesLocais[staff.user_id] || [];
        const idx = atual.indexOf(restauranteId);
        const nova = [...atual];
        if (idx > -1) nova.splice(idx, 1);
        else nova.push(restauranteId);
        this.$set(this.permissoesLocais, staff.user_id, nova);
      },

      resumoPerms(s) {
        const perms = this.permissoesLocais[s.user_id] || [];
        const total = this.restaurantes.length;
        if (!total) return "Sem restaurantes";
        if (perms.length === total) return `Todos (${total})`;
        if (perms.length === 0) return "Nenhum";
        return `${perms.length} de ${total}`;
      },

      abrirEditPerms(s) {
        this.$set(this.editandoPerms, s.user_id, true);
      },

      toggleMenu(userId) {
        this.menuAberto = this.menuAberto === userId ? null : userId;
      },

      fecharMenu() {
        this.menuAberto = null;
      },

      async alterarRole(s) {
        const novoRole = s.role === "staff" ? "manager" : "staff";
        const label = novoRole === "manager" ? "Manager" : "Staff";

        const confirmar = await swal.fire({
          title: `Mudar para ${label}?`,
          text:
            novoRole === "manager"
              ? `${s.username} passará a ter acesso total a todos os restaurantes e definições.`
              : `${s.username} passará a ter acesso apenas aos restaurantes que lhe atribuíres.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#ff825d",
          confirmButtonText: `Sim, mudar para ${label}`,
          cancelButtonText: "Cancelar",
        });
        if (!confirmar.value) return;

        this.$set(this.alterandoRole, s.user_id, true);
        try {
          await api.put(`/staff/${s.user_id}/role`, { role: novoRole });
          this.$notify({
            type: "success",
            message: `${s.username} é agora ${label}.`,
          });
          await this.carregar();
          this.menuAberto = null;
        } catch (err) {
          this.$notify({
            type: "danger",
            message: err.response?.data?.error || "Erro ao alterar role.",
          });
        } finally {
          this.$set(this.alterandoRole, s.user_id, false);
        }
      },

      async guardarPermissoes(staff) {
        this.$set(this.guardando, staff.user_id, true);
        try {
          await api.put(`/staff/${staff.user_id}/permissoes`, {
            restaurante_ids: this.permissoesLocais[staff.user_id] || [],
          });
          this.$set(this.editandoPerms, staff.user_id, false);
          this.$notify({
            type: "success",
            message: `Permissões de ${staff.username} guardadas.`,
          });
        } catch (err) {
          this.$notify({
            type: "danger",
            message: err.response?.data?.error || "Erro ao guardar.",
          });
        } finally {
          this.$set(this.guardando, staff.user_id, false);
        }
      },

      async convidar() {
        this.erroConvite = "";
        this.sucessoConvite = "";
        if (!this.emailConvite) return;

        // Confirmação extra para manager
        if (this.roleConvite === "manager") {
          const confirmar = await swal.fire({
            title: "Convidar como Manager?",
            html: `
              <p style="color:#6b6460;font-size:14px;margin-bottom:12px;">
                <strong>${this.emailConvite}</strong> terá acesso total com as seguintes permissões:
              </p>
              <ul style="text-align:left;color:#6b6460;font-size:13px;line-height:2;">
                <li>Todos os restaurantes</li>
                <li>Campanhas e templates</li>
                <li>Definições e categorias</li>
                <li>Gerir staff (convidar e revogar)</li>
              </ul>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ff825d",
            confirmButtonText: "Sim, enviar convite",
            cancelButtonText: "Cancelar",
          });
          if (!confirmar.value) return;
        }

        this.enviandoConvite = true;
        try {
          await api.post("/staff/convidar", {
            email: this.emailConvite,
            role: this.roleConvite,
          });
          this.sucessoConvite = `Convite de ${
            this.roleConvite === "manager" ? "Manager" : "Staff"
          } enviado para ${this.emailConvite}!`;
          this.emailConvite = "";
          await this.carregar();
        } catch (err) {
          this.erroConvite =
            err.response?.data?.error || "Erro ao enviar convite.";
        } finally {
          this.enviandoConvite = false;
        }
      },

      async cancelarConvite(convite) {
        try {
          await api.delete(`/staff/convite/${convite.id}`);
          this.$notify({ type: "success", message: "Convite cancelado." });
          await this.carregar();
        } catch (err) {
          this.$notify({
            type: "danger",
            message: "Erro ao cancelar convite.",
          });
        }
      },

      confirmarRevogar(staff) {
        this.staffParaRevogar = staff;
      },

      async revogar() {
        if (!this.staffParaRevogar) return;
        this.revogando = true;
        try {
          await api.delete(`/staff/${this.staffParaRevogar.user_id}`);
          this.$notify({
            type: "success",
            message: `Acesso de ${this.staffParaRevogar.username} revogado.`,
          });
          this.staffParaRevogar = null;
          await this.carregar();
        } catch (err) {
          this.$notify({
            type: "danger",
            message: err.response?.data?.error || "Erro ao revogar.",
          });
        } finally {
          this.revogando = false;
        }
      },

      formatarData(dt) {
        if (!dt) return "—";
        return new Date(dt).toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
  };
</script>

<style scoped>
  .gs-root {
    padding: 24px;
  }
  .gs-card {
    background: #fff;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  }

  .gs-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
    gap: 16px;
    flex-wrap: wrap;
  }
  .gs-card-header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .gs-card-icon {
    width: 44px;
    height: 44px;
    background: #fff7f5;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .gs-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1210;
    margin: 0;
  }
  .gs-sub {
    font-size: 13px;
    color: #8a7f7a;
    margin: 2px 0 0;
  }

  .gs-limite-badge {
    padding: 6px 16px;
    border-radius: 30px;
    font-size: 13px;
    font-weight: 600;
  }
  .badge-ok {
    background: #edfaf4;
    color: #27ae60;
  }
  .badge-warn {
    background: #fff3cd;
    color: #c67700;
  }

  .gs-section {
    margin-bottom: 32px;
  }
  .gs-section-title {
    font-size: 14px;
    font-weight: 700;
    color: #6b6460;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
  }
  .gs-count {
    font-weight: 400;
    color: #b0a8a0;
  }

  .gs-invite-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }
  .gs-input {
    flex: 1;
    min-width: 220px;
    border: 1.5px solid #e8e4e1;
    border-radius: 30px;
    padding: 10px 18px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s;
  }
  .gs-input:focus {
    border-color: #ff825d;
  }
  .gs-input:disabled {
    background: #f8f8f8;
    color: #b0a8a0;
  }

  /* Role selector */
  .gs-role-select {
    display: flex;
    border: 1.5px solid #e8e4e1;
    border-radius: 30px;
    overflow: hidden;
  }
  .gs-role-btn {
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    background: transparent;
    color: #8a7f7a;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .gs-role-btn.active {
    background: #ff825d;
    color: #fff;
  }

  .gs-role-desc {
    font-size: 13px;
    color: #6b6460;
    margin-top: 10px;
    background: #faf8f6;
    padding: 10px 16px;
    border-radius: 10px;
  }

  /* Role badges */
  .gs-role-badge {
    display: inline-block;
    width: fit-content;
    align-self: flex-start;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    margin-left: 0;
  }
  .badge-manager {
    background: #f0e8ff;
    color: #7c3aed;
  }
  .badge-staff {
    background: #e8f4ff;
    color: #2563eb;
  }

  .gs-btn-primary {
    background: #ff825d;
    color: #fff;
    border: none;
    border-radius: 30px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .gs-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .gs-btn-primary:hover:not(:disabled) {
    opacity: 0.85;
  }

  .gs-kebab-wrap {
    position: relative;
  }
  .gs-kebab-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1.5px solid #e8e4e1;
    background: #fff;
    cursor: pointer;
    font-size: 16px;
    color: #8a7f7a;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, border-color 0.15s;
  }
  .gs-kebab-btn:hover {
    background: #f5f3f1;
    border-color: #c8c4c0;
  }
  .gs-dropdown {
    position: absolute;
    right: 0;
    top: 38px;
    background: #fff;
    border: 1.5px solid #f0ece9;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    z-index: 100;
    min-width: 190px;
    overflow: hidden;
  }
  .gs-dropdown-item {
    display: block;
    width: 100%;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: background 0.12s;
  }
  .gs-dropdown-item:hover {
    background: #faf8f6;
  }
  .item-promote {
    color: #7c3aed;
  }
  .item-demote {
    color: #2563eb;
  }
  .item-save {
    color: #27ae60;
  }
  .item-danger {
    color: #e74c3c;
    border-top: 1px solid #f5f2ef;
  }

  .gs-btn-save-sm {
    background: #ff825d;
    color: #fff;
    border: none;
    border-radius: 30px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  .gs-btn-save-sm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .gs-btn-danger-sm {
    background: transparent;
    color: #e74c3c;
    border: 1.5px solid #e74c3c;
    border-radius: 30px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .gs-btn-danger-sm:hover {
    background: #e74c3c;
    color: #fff;
  }
  .gs-btn-neutral {
    background: #f5f3f1;
    color: #6b6460;
    border: none;
    border-radius: 30px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  .gs-btn-danger {
    background: #e74c3c;
    color: #fff;
    border: none;
    border-radius: 30px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .gs-btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .gs-warn-text {
    color: #c67700;
    font-size: 13px;
    margin-top: 8px;
  }
  .gs-error-text {
    color: #e74c3c;
    font-size: 13px;
    margin-top: 8px;
  }
  .gs-success-text {
    color: #27ae60;
    font-size: 13px;
    margin-top: 8px;
  }

  .gs-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .gs-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
    border: 1.5px solid #f0ece9;
    border-radius: 16px;
    background: #fdfcfc;
    flex-wrap: wrap;
  }
  .gs-item-pending {
    border-style: dashed;
    background: #fffdf9;
  }

  .gs-item-avatar {
    width: 40px;
    height: 40px;
    background: #ff825d;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
  }
  .gs-item-avatar.avatar-manager {
    background: #7c3aed;
  }
  .gs-item-avatar-pending {
    background: #f0ece9;
    font-size: 18px;
  }

  .gs-item-info {
    flex: 1;
    min-width: 160px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .gs-item-nome-row {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }
  .gs-item-nome {
    font-weight: 700;
    color: #1a1210;
    font-size: 14px;
  }
  .gs-item-sub {
    font-size: 12px;
    color: #8a7f7a;
  }
  .gs-nunca {
    color: #c0b8b4;
  }

  .gs-perms {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 2;
    min-width: 200px;
    align-items: center;
  }
  .gs-perms-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
  }
  .gs-perms-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    border-radius: 30px;
    background: #f5f3f1;
    color: #6b6460;
    font-size: 13px;
    font-weight: 600;
    border: 1.5px solid #e8e4e1;
    transition: background 0.15s;
  }
  .gs-perms-summary:hover .gs-perms-pill {
    background: #edfaf4;
    border-color: #27ae60;
    color: #27ae60;
  }
  .gs-perms-edit-hint {
    font-size: 12px;
    color: #b0a8a0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .gs-perms-expanded {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .gs-manager-access {
    color: #27ae60;
    font-size: 13px;
    font-weight: 500;
  }
  .gs-perm-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s, color 0.15s;
    border: 1.5px solid transparent;
  }
  .perm-on {
    background: #edfaf4;
    color: #27ae60;
    border-color: #27ae60;
  }
  .perm-off {
    background: #f5f3f1;
    color: #b0a8a0;
    border-color: #e8e4e1;
  }
  .gs-perm-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
  .gs-no-rest {
    font-size: 12px;
    color: #b0a8a0;
  }

  .gs-item-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-end;
  }
  .gs-empty {
    text-align: center;
    padding: 32px;
    color: #b0a8a0;
    font-size: 14px;
  }

  .gs-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 48px 0;
    justify-content: center;
    color: #8a7f7a;
    font-size: 14px;
  }
  .gs-loading-spinner {
    width: 20px;
    height: 20px;
    border: 2.5px solid #f0ece9;
    border-top-color: #ff825d;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .gs-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
  .gs-modal {
    background: #fff;
    border-radius: 20px;
    padding: 32px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  }
  .gs-modal h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1a1210;
    margin-bottom: 12px;
  }
  .gs-modal p {
    color: #6b6460;
    font-size: 14px;
    margin-bottom: 8px;
  }
  .gs-modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 24px;
  }
</style>
